import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import {
    AccountRecord,
    ContentAction,
    ContentActionView,
    ContentView,
    EnrichedContent,
    ProfileViewWithShadowBan,
    useGetAccountQuery,
    useUpdateEngageAccountIdMutation
} from '../../api/client'
import { canUseCrossProfileEngageState } from '../../store/DomainStore'
import Modal from '../Modal'
import Spinner from '../Spinner'
import ContentCard from '../content/content-card/ContentCard'
import NewReply from './NewReply'
import ReplyUs from './ReplyUs'
import SuggestedRepliesList from './SuggestedRepliesList'
import SuggestedRepliesUpsell from './SuggestedRepliesUpsell'
import { toastError, toastSuccess } from '../Notification'
import { getAccountDisplayName } from '../../util/account-utils'
import { isActionApplied } from '../content/content-card/actions'

type Props = {
    open: boolean
    setOpen: (open: boolean) => void
    content: ContentView
    onReply: (params: {
        message: string
        suggestedReplies?: string[]
        actionByAccountIdOverride?: number
    }) => void
    savingReply: boolean
    onDeleteReply: (actionId: number) => void
    deletingReply: boolean
    onActionClick: (contentActions: ContentActionView[]) => void
    profileCache: ProfileViewWithShadowBan[]
    actionCacheMap: Map<number, ContentActionView[]>
}

/**
 * Modal that allows the user to reply and react to content.
 */
export default function ReplyModal(props: Props) {
    const {
        open,
        setOpen,
        content,
        onReply,
        savingReply,
        onDeleteReply,
        deletingReply,
        onActionClick,
        profileCache,
        actionCacheMap
    } = props

    // Identify the relevant actions from the action cache map
    const allExistingContentActions = actionCacheMap.get(content.id) || []

    const replyDeleted = isActionApplied(allExistingContentActions, ContentAction.Delete)
    const replyHidden = isActionApplied(allExistingContentActions, ContentAction.Hide)

    const replyDisabled = replyDeleted || replyHidden

    const replyDisabledMessage = replyDeleted
        ? 'Reply is disabled because this content was deleted.'
        : replyHidden
          ? 'Reply is disabled while this content is hidden. Unhide it first.'
          : undefined

    const replyActions = allExistingContentActions.filter(
        (action) => action.action === ContentAction.Reply && action.active
    )

    const userHasResponded = replyActions.length > 0

    const [selectedReply, setSelectedReply] = useState('')

    const [suggestedReplies, setSuggestedReplies] = useState<string[]>()

    const llmRepliesEnabled = content.account.llmEnableSuggestedReplies || false

    const canUseCrossProfileEngageToggle = useRecoilValue(canUseCrossProfileEngageState)

    const primaryAccount: AccountRecord = content.account

    // Cross-profile reply is not supported by the Instagram/Facebook API, nor by YouTube
    const shortName = primaryAccount.socialMediaServiceShortName?.toLowerCase()
    const crossProfileUnsupported = shortName === 'instagram' || shortName === 'youtube'
    const canUseCrossProfileEngage = canUseCrossProfileEngageToggle && !crossProfileUnsupported

    const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(
        primaryAccount.engageAccountId ?? primaryAccount.id
    )
    const [defaultAccountId, setDefaultAccountId] = useState<number | undefined>(
        primaryAccount.engageAccountId ?? undefined
    )

    const [updateEngageAccountIdMutation] = useUpdateEngageAccountIdMutation()

    const effectiveDefaultAccountId = useMemo(() => {
        if (!canUseCrossProfileEngage) {
            return primaryAccount.id
        }

        return defaultAccountId ?? primaryAccount.engageAccountId ?? primaryAccount.id
    }, [
        canUseCrossProfileEngage,
        defaultAccountId,
        primaryAccount.engageAccountId,
        primaryAccount.id
    ])

    const shouldFetchDefaultAccount =
        canUseCrossProfileEngage && effectiveDefaultAccountId !== primaryAccount.id

    const { data: defaultAccountData } = useGetAccountQuery({
        variables: { accountId: effectiveDefaultAccountId },
        skip: !shouldFetchDefaultAccount,
        fetchPolicy: 'cache-first'
    })

    const defaultAccount = defaultAccountData?.getAccount ?? null

    const formatAccountLabel = useCallback((account: AccountRecord) => {
        return getAccountDisplayName(account)
    }, [])

    const [selectedAccountLabel, setSelectedAccountLabel] = useState<string | undefined>(
        formatAccountLabel(primaryAccount)
    )

    useEffect(() => {
        if (!canUseCrossProfileEngage) {
            setSelectedAccountId(primaryAccount.id)
            setDefaultAccountId(undefined)
            setSelectedAccountLabel(formatAccountLabel(primaryAccount))
            return
        }

        setSelectedAccountId(primaryAccount.engageAccountId ?? primaryAccount.id)
        setDefaultAccountId(primaryAccount.engageAccountId ?? undefined)
    }, [
        canUseCrossProfileEngage,
        formatAccountLabel,
        primaryAccount,
        primaryAccount.engageAccountId,
        primaryAccount.id
    ])

    useEffect(() => {
        if (!canUseCrossProfileEngage) {
            setSelectedAccountLabel(formatAccountLabel(primaryAccount))
            return
        }

        if (selectedAccountId !== undefined && selectedAccountId !== effectiveDefaultAccountId) {
            return
        }

        if (effectiveDefaultAccountId === primaryAccount.id) {
            setSelectedAccountLabel(formatAccountLabel(primaryAccount))
            return
        }

        if (defaultAccount) {
            setSelectedAccountLabel(formatAccountLabel(defaultAccount))
        }
    }, [
        canUseCrossProfileEngage,
        defaultAccount,
        effectiveDefaultAccountId,
        formatAccountLabel,
        primaryAccount,
        selectedAccountId
    ])

    const handleCrossProfileAccountSelect = useCallback(
        (account: AccountRecord) => {
            if (!canUseCrossProfileEngage) {
                return
            }
            setSelectedAccountId(account.id)
            setSelectedAccountLabel(formatAccountLabel(account))
        },
        [canUseCrossProfileEngage, formatAccountLabel]
    )

    const handleCrossProfileDefaultChange = useCallback(
        async (account: AccountRecord) => {
            if (!canUseCrossProfileEngage) {
                return
            }
            if (defaultAccountId === account.id) {
                return
            }

            try {
                await updateEngageAccountIdMutation({
                    variables: {
                        accountId: primaryAccount.id,
                        engageAccountId: account.id
                    }
                })
                setDefaultAccountId(account.id)
                setSelectedAccountId(account.id)
                setSelectedAccountLabel(formatAccountLabel(account))
                toastSuccess(`${formatAccountLabel(account)} set as your default reply profile`)
            } catch (error) {
                toastError((error as Error).message)
            }
        },
        [
            canUseCrossProfileEngage,
            defaultAccountId,
            formatAccountLabel,
            primaryAccount.id,
            updateEngageAccountIdMutation
        ]
    )

    const effectiveAccountId = useMemo(() => {
        if (!canUseCrossProfileEngage) {
            return undefined
        }

        return selectedAccountId ?? defaultAccountId ?? primaryAccount.id
    }, [canUseCrossProfileEngage, defaultAccountId, primaryAccount.id, selectedAccountId])

    const handleDeleteReply = async (actionId: number) => {
        onDeleteReply(actionId)
    }

    const handleReplySubmit = useCallback(
        async (message: string) => {
            if (
                canUseCrossProfileEngage &&
                effectiveAccountId !== undefined &&
                effectiveAccountId !== defaultAccountId
            ) {
                try {
                    await updateEngageAccountIdMutation({
                        variables: {
                            accountId: primaryAccount.id,
                            engageAccountId: effectiveAccountId
                        }
                    })
                    setDefaultAccountId(effectiveAccountId)
                } catch (error) {
                    toastError((error as Error).message)
                }
            }

            onReply({
                message,
                suggestedReplies,
                actionByAccountIdOverride: canUseCrossProfileEngage ? effectiveAccountId : undefined
            })
        },
        [
            canUseCrossProfileEngage,
            defaultAccountId,
            effectiveAccountId,
            onReply,
            primaryAccount.id,
            suggestedReplies,
            updateEngageAccountIdMutation
        ]
    )

    return (
        <Modal
            open={open}
            onClose={() => {
                setSuggestedReplies([])
                setOpen(false)
            }}
            title={
                <span className="inline-flex items-center gap-4">
                    Your replies
                    {(savingReply || deletingReply) && <Spinner size={6} />}
                </span>
            }
            icon={<ChatBubbleLeftRightIcon className="h-8 w-8 text-ink-700" aria-hidden="true" />}
            size="fit"
            overflowVisible={true}
        >
            <div className="mt-8 mb-2 min-w-[750px]">
                <div className="hover:bg-ink-50 border border-ink-200 rounded-lg shadow-md">
                    <ContentCard
                        content={content}
                        showAccount={false}
                        onContentAction={(contentActions: ContentActionView[]) =>
                            onActionClick(contentActions)
                        }
                        enriching={false}
                        detectedLanguage={null}
                        translating={false}
                        onTranslateClick={() => {}}
                        profileCache={profileCache}
                        enrichedContentMap={new Map<number, EnrichedContent>()}
                        actionCacheMap={actionCacheMap}
                        rounded={true}
                        isSelected={false}
                        showReplyButton={false}
                        // Do not define a function for selection change.
                        // This will disable the selection feature in the ContentCard
                        onSelectionChange={() => {}}
                        onMarkAsReadClick={() => {}}
                        onMarkAsUnreadClick={() => {}}
                    />
                </div>

                <div className="border-ink-400 w-16 h-20 border-l-2 border-b-2 border-dashed absolute rounded-bl-lg left-16"></div>

                {replyActions?.map((replyAction) => {
                    return (
                        <div
                            className="mt-10 ml-8 opacity-100 relative border border-ink-200 hover:bg-ink-50 shadow-md rounded-lg"
                            key={replyAction.id}
                        >
                            <ReplyUs
                                content={content}
                                action={replyAction}
                                onDelete={handleDeleteReply}
                            />
                        </div>
                    )
                })}
                {!userHasResponded && (
                    <div className="mb-4">
                        <NewReply
                            onReply={handleReplySubmit}
                            sendingReply={savingReply}
                            initialReply={selectedReply}
                            content={content}
                            canUseCrossProfileEngage={canUseCrossProfileEngage}
                            currentAccount={primaryAccount}
                            selectedAccountId={
                                canUseCrossProfileEngage ? selectedAccountId : primaryAccount.id
                            }
                            defaultAccountId={
                                canUseCrossProfileEngage ? defaultAccountId : undefined
                            }
                            selectedAccountLabel={
                                canUseCrossProfileEngage ? selectedAccountLabel : undefined
                            }
                            onAccountSelect={
                                canUseCrossProfileEngage
                                    ? handleCrossProfileAccountSelect
                                    : undefined
                            }
                            onMakeDefault={
                                canUseCrossProfileEngage
                                    ? handleCrossProfileDefaultChange
                                    : undefined
                            }
                            replyDisabled={replyDisabled}
                            replyDisabledMessage={replyDisabledMessage}
                        />
                    </div>
                )}
                {!llmRepliesEnabled && <SuggestedRepliesUpsell />}
                {llmRepliesEnabled && !userHasResponded && (
                    <SuggestedRepliesList
                        onSelection={setSelectedReply}
                        suggestedReplies={suggestedReplies}
                        setSuggestedReplies={setSuggestedReplies}
                        content={content}
                    />
                )}
            </div>
        </Modal>
    )
}
